// src/components/CloneCommandsDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { cloneCommands } from '../services/exerciseService';

/**
 * Clone commands from a chosen source exercise into `targetExercise`.
 * Only source exercises other than the target are selectable.
 * Cloned commands become fresh, independent records in the target.
 */
const CloneCommandsDialog = ({
  open,
  targetExercise,   // the DRAFT exercise we clone INTO
  allExercises,     // full list, to build the source dropdown
  onClose,
  onSuccess,
  onNotify,
}) => {
  const [sourceId, setSourceId] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setSourceId('');
      setError('');
    }
  }, [open]);

  // Selectable sources: every exercise except the target itself,
  // and only those that actually have commands to clone.
  const sourceOptions = (allExercises || []).filter(
    (ex) => ex.id !== targetExercise?.id && (ex._count?.commands || 0) > 0
  );

  const handleClone = async () => {
    if (!sourceId) {
      setError('Please select a source exercise');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await cloneCommands(targetExercise.id, sourceId);
      const { copied = 0, skipped = 0 } = res?.data || {};
      if (onNotify) {
        onNotify(
          `Cloned ${copied} command(s) into "${targetExercise.name}"` +
            (skipped ? `, skipped ${skipped} duplicate(s)` : ''),
          'success'
        );
      }
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to clone commands';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Clone Commands</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copy all commands from another exercise into{' '}
            <strong>{targetExercise?.name}</strong>. The copied commands become
            fresh, independent records here — the source exercise is not changed.
            Any command whose code already exists here will be skipped.
          </Typography>

          {sourceOptions.length === 0 ? (
            <Alert severity="info">
              There are no other exercises with commands to clone from.
            </Alert>
          ) : (
            <TextField
              fullWidth
              select
              label="Clone commands from"
              value={sourceId}
              onChange={(e) => {
                setSourceId(e.target.value);
                setError('');
              }}
              margin="normal"
              disabled={loading}
            >
              {sourceOptions.map((ex) => (
                <MenuItem key={ex.id} value={ex.id}>
                  {ex.year} — {ex.name} ({ex._count?.commands || 0} commands)
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleClone}
          variant="contained"
          disabled={loading || sourceOptions.length === 0 || !sourceId}
        >
          {loading ? <CircularProgress size={24} /> : 'Clone Commands'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneCommandsDialog;